import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import MasterLayout from '../../components/layouts/masterlayout';
import ErrorMessage from '../../components/common/errormessage';
import SuccessMessage from '../../components/common/successmessage';
import DietaryRestrictionDropdown from '../common/dropdowns/dropdown_dietary_restrictions';
import TypeDropdown from '../common/dropdowns/dropdown_item_types';
import CategoryDropdown from '../common/dropdowns/dropdown_item_category';
import GeneralTextBox from '../common/textboxes/textbox_general';
import GeneralButton from '../common/button_general';
import ImageUploadForm from '../common/imageuploadform';
import { useNavigate } from 'react-router-dom';
import { getHeaders, handleResponse, handleErrorResponse } from '../../lib/requests';
import Spinner from '../common/spinner';
import StockStatusDropdown from '../common/dropdowns/dropdown_stock_status';

const parseDietaryRestrictions = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    const trimmed = data.replace(/^{|}$/g, '');
    return trimmed ? trimmed.split(',').map(s => s.trim()) : [];
  }
  return [];
};

function ItemForm({ item = {} }) {
  const { itemId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const handleBack = () => navigate('/items');

  const [formData, setFormData] = useState({
    item_id: itemId || '',
    name: item.name || '',
    type: item.type || '',
    category: item.category || '',
    dietary_restriction_type: parseDietaryRestrictions(item.dietary_restriction_type),
    item_cost: item.item_cost || '',
    default_price: item.default_price || '',
    reference_id: item.reference_id || '',
    allergen_note: item.allergen_note || '',
    stock_status: item.stock_status || 'in_stock',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStockStatusSelect = (status) => {
    setFormData({ ...formData, stock_status: status });
  };

  useEffect(() => {
    const fetchItemData = async () => {
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        setIsLoading(true);
        const response = await axios.get(`${apiUrl}/items/${itemId}`, { headers: getHeaders(apiToken) });
        const itemData = response.data.data;
        setFormData({
          name: itemData.name || '',
          type: itemData.type || '',
          category: itemData.category || '',
          dietary_restriction_type: parseDietaryRestrictions(itemData.dietary_restriction_type),
          item_cost: itemData.item_cost || '',
          default_price: itemData.default_price || '',
          reference_id: itemData.reference_id || '',
          allergen_note: itemData.allergen_note || '',
          image_url: itemData.image_url || '',
          stock_status: itemData.stock_status || 'in_stock',
        });
        setShowError(false);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (itemId) {
      fetchItemData();
    }
  }, [itemId]);

  const resetForm = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowError(false);
    setShowSuccess(false);
  };

  const handleDietaryRestrictionSelect = (selectedItems) => {
    setFormData({ ...formData, dietary_restriction_type: selectedItems.map(item => item.name) });
  };

  const handleCategorySelect = (item) => {
    setFormData({ ...formData, category: item.name });
  };

  const handleTypeSelect = (selectedItem) => {
    setFormData({
      ...formData,
      type: selectedItem.name,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiToken = localStorage.getItem('accessToken') || '';
    const apiUrl = process.env.REACT_APP_API_URL || '';

    resetForm();

    if (!apiToken || !apiUrl) {
      console.error('API token or URL is missing');
      setShowError(true);
      setErrorMessage('Configuration error');
      return;
    }

    const headers = getHeaders(apiToken);
    const url = itemId ? `${apiUrl}/items/${itemId}` : `${apiUrl}/items?pageSize=1000`;
    const method = itemId ? 'put' : 'post';

    try {
      const response = await axios({
        method,
        url,
        data: JSON.stringify(formData),
        headers,
      });

      handleResponse(
        response,
        "Item",
        setShowSuccess,
        setSuccessMessage,
        setShowError,
        setErrorMessage,
        navigate,
        '/items/:id'
      );
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const pageTitle = itemId ? 'Edit ' + formData.name : 'Create Item';

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && (
        <ErrorMessage
          message={errorMessage}
          linkText=""
          linkHref="/retry-url"
        />
      )}
      {showSuccess && (
        <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />
      )}
      {isLoading && <Spinner />}
      {!isLoading && (
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid gap-6 mb-6">
            <div className="grid gap-6 grid-cols-1 md:grid-cols-4">
              <GeneralTextBox
                label="Name"
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Name"
                required={true}
              />
              <GeneralTextBox
                label="Item Cost"
                id="item_cost"
                name="item_cost"
                type="number"
                value={formData.item_cost}
                onChange={handleChange}
                placeholder="Item Cost"
                required={true}
              />
              <GeneralTextBox
                label="Default Price"
                id="default_price"
                name="default_price"
                type="number"
                value={formData.default_price}
                onChange={handleChange}
                placeholder="Default Price"
                required={true}
              />
              <GeneralTextBox
                label="Reference ID"
                id="reference_id"
                name="reference_id"
                type="text"
                value={formData.reference_id}
                onChange={handleChange}
                placeholder="Reference ID"
              />
            </div>
            <div className="grid gap-6 grid-cols-1 md:grid-cols-4">
              <StockStatusDropdown
                onSelect={handleStockStatusSelect}
                selectedStatus={formData.stock_status}
              />
              <TypeDropdown
                onSelect={handleTypeSelect}
                selectedType={formData.type}
              />
              <CategoryDropdown
                onSelect={handleCategorySelect}
                selectedCategory={formData.category}
              />
              <DietaryRestrictionDropdown
                onSelect={handleDietaryRestrictionSelect}
                selectedRestrictions={formData.dietary_restriction_type}
                singleSelect={false}
              />
            </div>
          </div>
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
            <GeneralTextBox
              label="Allergen Note"
              id="allergen_note"
              name="allergen_note"
              type="text"
              value={formData.allergen_note}
              onChange={handleChange}
              placeholder="Enter allergen note"
              multiline={true}
              rows={2}
            />
            <div></div>
          </div>
          {formData.image_url && (
            <img src={formData.image_url} alt="Item Image" style={{ width: '150px', height: 'auto' }} />
          )}
          <ImageUploadForm setFormData={setFormData} />
          <div className="flex justify-center mt-6">
            <GeneralButton type="submit" buttonType="primary">
              {itemId ? 'Update Item' : 'Create Item'}
            </GeneralButton>
            <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
              Back to Item List
            </GeneralButton>
          </div>
        </form>
      )}
    </MasterLayout>
  );
}

ItemForm.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    category: PropTypes.string,
    dietary_restriction_type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    item_cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    default_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reference_id: PropTypes.string,
    allergen_note: PropTypes.string,
  }),
};

export default ItemForm;
